import React, { useState, useEffect, useMemo } from 'react';
import ReactLoading from 'react-loading';
import classnames from 'classnames';
import ReactTableWrapper from './ReactTableWrapper';
import SectionPagination from './SectionPagination';
import useManualReactTableState from './ManualReactTableState';
import OrderListRefineResults from './OrderListRefineResults';
import {
    convertDataToListingDisplay,
    convertDispatchListingToAReadableFormat,
    getGraphQLTotalRecords,
} from '../selectors/converterSelector';
import {
    axiosRequestDispatchList,
    axiosPackOrder,
    axiosAwaitingPickupOrder,
    axiosUnpackOrder,
    axiosSubmitManifest
} from '../actions/dispatchActions';
import LocalizedMessage from 'react-l10n';
import { breakpoints, getBreakpoint } from '../dom/_site';
import { handleAxiosErrors } from '../_utils';
import { getTranslation } from '../lang/main';
import FieldSelect from './fields/FieldSelect';
import InlineFieldGroup from './fields/InlineFieldGroup';
import RefineResultsButton from './common/RefineResultsButton';
import OrderListExportButton from './common/OrderListExportButton';
import { createAlert } from '../dom/window';

const shippingStatusDropdown = [
    {
        label: getTranslation('UNPACKED'),
        value: '',
    },
    {
        label: getTranslation('PACKED'),
        value: 'Packed',
    },
    {
        label: getTranslation('READY_FOR_COLLECTION'),
        value: 'Awaiting Pickup',
    },
];

/**
 * Memoized Row Component to avoid unnecessary re-renders
 */
const MemoizedRow = React.memo(({ row, userInput, saveShippingStatus, clearUserInput, updateUserInput, isUserInputLoading }) => {
    const identifier = row.original.ID;
    const inputKey = 'ShippingStatus';
    let hasInlineEdit = false;
    let currentValue = row.original[inputKey];

    if (typeof row.value == 'undefined' || row.value === null) {
        return '';
    }

    if (typeof userInput[identifier] != 'undefined' && typeof userInput[identifier][inputKey] != 'undefined') {
        hasInlineEdit = currentValue != userInput[identifier][inputKey];
        currentValue = userInput[identifier][inputKey];
    }

    return (
        <React.Fragment>
            <span className='react-table__info react-table__info--default'>
                <InlineFieldGroup
                    hasUndo={true}
                    isModified={hasInlineEdit}
                    saveButtonLoading={isUserInputLoading(identifier, inputKey)}
                    onSaveClick={() => saveShippingStatus(identifier, inputKey, currentValue)}
                    onUndoClick={() => clearUserInput(identifier, inputKey)}
                >
                    <FieldSelect
                        label={getTranslation('STATUS')}
                        animatedLabel={getTranslation('STATUS')}
                        value={currentValue}
                        name={inputKey}
                        options={shippingStatusDropdown}
                        onChange={(e) => updateUserInput(identifier, e.target.name, e.target.value)}
                    />
                </InlineFieldGroup>
            </span>
        </React.Fragment>
    );
});

function DispatchList(props) {
    let mainTableWrapper;

    const {
        className,
        requestPropWatcher = '',
        sortBy = 'ID',
        sortDirection = 'DESC',
        columnsToExportMap = [
            { key: 'InternalItemID', column: 'Order Number/ID' },
            { key: 'DeliverTo', column: 'Deliver To' },
            { key: 'ContainItemTypes', column: 'Contains' },
            { key: 'ShippingMethod', column: 'Carrier' },
            { key: 'Placed', column: 'Data Ordered' },
            { key: 'PostmenLabelUrl', column: 'Label' },
            { key: 'Status', column: 'Order Status' },
            { key: 'ShippingStatus', column: 'Dispatch Status' },
            { key: 'BoxPackerParcelCount', column: 'No. Articles' },
            { key: 'PostmenTrackingInfo', column: 'Tracking' }
        ]
    } = props;
    const refineResultsModalKey =
        props.refineResultsModalKey || 'refine-results-order-list';

    // State management
    const mrtState = useManualReactTableState({
        listParams: {
            perPage: 99999,
            sortBy,
            sortDirection,
        },
    });

    // Core
    const { isLoading, toggleLoading } = mrtState;
    const {
        list,
        listParams,
        getDisplayedList,
        setListDisplay,
        updateList,
    } = mrtState;
    const { getFilters, setFilters } = mrtState;
    const {
        getTotalRecords,
        setTotalRecords,
        getActivePage,
        setActivePage,
        getTotalPages,
        getPaginationPages,
    } = mrtState;
    const { handleOnTableUpdate } = mrtState;
    const listEffectWatcher = mrtState.getEffectWatcher();

    // User Input
    const { isUserInputLoading, toggleUserInputLoading } = mrtState;
    const { userInput, clearUserInput, updateUserInput } = mrtState;

    // Manifest
    const [isManifestLoading, toggleManifestLoading] = useState(false);

    // Batched update state
    const [batchLoading, setBatchLoading] = useState(false);
    const [tempFilteredData, setTempFilteredData] = useState(null);

    // Effects
    // - Main Request
    useEffect(() => {
        request();
    }, [listEffectWatcher, requestPropWatcher]);

    // Memoize the filtered list to avoid unnecessary re-renders
    const filteredList = useMemo(() => {
        if (batchLoading) return [];

        return getDisplayedList();
    }, [getDisplayedList(), batchLoading]);

    // Functions
    const request = () => {
        toggleLoading('list', true);
        let updatedListParams = JSON.parse(JSON.stringify(listParams));
        console.log('updatedListParams ==> ', updatedListParams, "listParams ==> ", listParams);
        return axiosRequestDispatchList(updatedListParams)
            .then((response) => {
                const list = convertDispatchListingToAReadableFormat(
                    response.data.data.readUnManifestedOrders
                );
                const listingDisplay = convertDataToListingDisplay(
                    response.data.data.readUnManifestedOrders,
                    'ID'
                );
                const total = getGraphQLTotalRecords(
                    response.data.data.readUnManifestedOrders
                );

                console.log('list', list);

                // Batch updates
                setBatchLoading(true);
                setTempFilteredData({ listingDisplay, list, total });
                setTimeout(() => {
                    Promise.all([
                        setListDisplay(listingDisplay),
                        updateList(list),
                        setTotalRecords(total),
                    ]).then(() => {
                        setBatchLoading(false);
                        toggleLoading('list', false);
                    });
                }, 0);
            })
            .catch((error) => {
                handleAxiosErrors(error);
                toggleLoading('list', false);
            });
    };

    const toggleRefineResultsModal = (value) => {
        if (value) {
            $('#' + refineResultsModalKey).modal('show');
        } else {
            $('#' + refineResultsModalKey).modal('hide');
        }
    };

    const saveShippingStatus = (identifier, key, selectedShipping) => {
        let axiosCall;
        let graphqlName = '';

        switch (selectedShipping) {
            case 'Packed':
                axiosCall = axiosPackOrder;
                graphqlName = 'packOrder';
                break;
            case 'Awaiting Pickup':
                axiosCall = axiosAwaitingPickupOrder;
                graphqlName = 'awaitingPickupOrder';
                break;
            default:
                axiosCall = axiosUnpackOrder;
                graphqlName = 'unpackOrder';
                break;
        }

        if (!axiosCall) return;

        toggleUserInputLoading(identifier, key, true);

        return axiosCall(identifier).then((response) => {
            const { data } = response;

            toggleUserInputLoading(identifier, key, false);

            console.log(data);

            if (typeof data.data[graphqlName] != 'undefined') {
                let updatedList = {
                    [identifier]: {
                        ShippingStatus: data.data[graphqlName].ShippingStatus,
                    },
                };

                console.log('Running updateList', updatedList);

                Promise.all([updateList(updatedList)]).then(() => {
                    clearUserInput(identifier, key);
                });
            } else {
                clearUserInput(identifier, key);
            }
        })
            .catch((error) => {
                handleAxiosErrors(error);
            })
            .finally(() => {
                toggleUserInputLoading(identifier, key, false);
            });
    };

    const submitManifest = (e) => {
        e.preventDefault();

        if (isManifestLoading) return;

        toggleManifestLoading(true);

        return axiosSubmitManifest().then((response) => {
            return request();
        })
            .catch((error) => {
                handleAxiosErrors(error);
            })
            .finally(() => {
                toggleManifestLoading(false);
            });
    };

    const getTableProps = () => {
        let tableProps = {
            list: {
                columns: [
                    {
                        Header: () => (
                            <React.Fragment>
                                <span className='react-table__heading hidden-xs'>
                                    <LocalizedMessage id={'ORDER_NUMBER'} />
                                </span>
                                <span className='react-table__heading visible-xs'>
                                    <LocalizedMessage id={'ORDER_NO'} />
                                </span>
                                <span className='react-table__sort-icon' />
                            </React.Fragment>
                        ),
                        Cell: (row) => (
                            <React.Fragment>
                                <span className='react-table__info'>
                                    <a href={`${window.baseURL}checkout/confirmation/${row.original.ID}/?type=dispatch-orders`}>{row.value}</a>
                                </span>
                            </React.Fragment>
                        ),
                        accessor: 'InternalItemID',
                    },
                    {
                        Header: () => (
                            <React.Fragment>
                                <span className='react-table__heading'>
                                    <LocalizedMessage id={'DELIVER_TO'} />
                                </span>
                                <span className='react-table__sort-icon' />
                            </React.Fragment>
                        ),
                        Cell: (row) => (
                            <React.Fragment>
                                <span className='react-table__info'>
                                    {row.value}
                                </span>
                            </React.Fragment>
                        ),
                        accessor: 'DeliverTo',
                    },
                    {
                        Header: () => (
                            <React.Fragment>
                                <span className='react-table__heading'>
                                    <LocalizedMessage id={'CONTAINS'} />
                                </span>
                                <span className='react-table__sort-icon' />
                            </React.Fragment>
                        ),
                        Cell: (row) => (
                            <React.Fragment>
                                <span className='react-table__info'>
                                    {row.value}
                                </span>
                            </React.Fragment>
                        ),
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        accessor: 'ContainItemTypes',
                    },
                    {
                        Header: () => (
                            <React.Fragment>
                                <span className='react-table__heading'>
                                    <LocalizedMessage id={'CARRIER'} />
                                </span>
                                <span className='react-table__sort-icon' />
                            </React.Fragment>
                        ),
                        Cell: (row) => (
                            <React.Fragment>
                                <span className='react-table__info'>
                                    {row?.original?.TrackingNumber ? '' : row.value}
                                </span>
                            </React.Fragment>
                        ),
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        accessor: 'ShippingMethod',
                    },
                    {
                        Header: () => (
                            <React.Fragment>
                                <span className='react-table__heading'>
                                    <LocalizedMessage id={'DATE_ORDERED'} />
                                </span>
                                <span className='react-table__sort-icon' />
                            </React.Fragment>
                        ),
                        Cell: (row) => (
                            <React.Fragment>
                                <span className='react-table__info'>
                                    {row.original.PlacedNice}
                                </span>
                            </React.Fragment>
                        ),
                        accessor: 'Placed',
                    },
                    {
                        Header: () => (
                            <React.Fragment>
                                <span className='react-table__heading'>
                                    <LocalizedMessage id={'LABEL'} />
                                </span>
                            </React.Fragment>
                        ),
                        Cell: (row) => {
                            let downloadLink = row.value ? (
                                <a href={row.value} target="_blank"><LocalizedMessage id={'DOWNLOAD'} /></a>
                            ) : null;
                            if (!row.value) {
                                return (
                                    <React.Fragment>
                                        <span className='react-table__info'>
                                            <LocalizedMessage id={'N_A'} />
                                        </span>
                                    </React.Fragment>
                                );
                            }

                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row?.original?.TrackingNumber ? null : downloadLink}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        accessor: 'PostmenLabelUrl',
                    },
                    {
                        Header: () => (
                            <React.Fragment>
                                <span className='react-table__heading'>
                                    <LocalizedMessage id={'ORDER_STATUS'} />
                                </span>
                            </React.Fragment>
                        ),
                        Cell: (row) => (
                            <React.Fragment>
                                <span className='react-table__info'>
                                    {row.value}
                                </span>
                            </React.Fragment>
                        ),
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        sortable: false,
                        accessor: 'Status',
                    },
                    {
                        Header: () => (
                            <React.Fragment>
                                <span className='react-table__heading'>
                                    <LocalizedMessage id={'DISPATCH_STATUS'} />
                                </span>
                                <span className='react-table__sort-icon' />
                            </React.Fragment>
                        ),
                        Cell: (row) => (
                            <MemoizedRow
                                row={row}
                                userInput={userInput}
                                saveShippingStatus={saveShippingStatus}
                                clearUserInput={clearUserInput}
                                updateUserInput={updateUserInput}
                                isUserInputLoading={isUserInputLoading}
                            />
                        ),
                        accessor: 'ShippingStatus',
                    },
                    {
                        Header: () => (
                            <React.Fragment>
                                <span className='react-table__heading'>
                                    <LocalizedMessage id={'NO_OF_ARTICLES'} />
                                </span>
                                <span className='react-table__sort-icon' />
                            </React.Fragment>
                        ),
                        Cell: (row) => (
                            <React.Fragment>
                                <span className='react-table__info'>
                                    {row.value}
                                </span>
                            </React.Fragment>
                        ),
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        accessor: 'BoxPackerParcelCount',
                    },
                    {
                        Header: () => (
                            <React.Fragment>
                                <span className='react-table__heading'>
                                    <LocalizedMessage id={'TRACKING'} />
                                </span>
                                <span className='react-table__sort-icon' />
                            </React.Fragment>
                        ),
                        Cell: (row) => (
                            <React.Fragment>
                                <span className='react-table__info' style={{ wordBreak: 'break-all' }}>
                                    {row.original.TrackingNumber ?? row.value}
                                </span>
                            </React.Fragment>
                        ),
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        accessor: 'PostmenTrackingInfo',
                    },
                    {
                        expander: true,
                        Expander: ({ isExpanded, ...rest }) => (
                            <React.Fragment>
                                <button
                                    type='button'
                                    className='react-table__extra-data-toggle'
                                >
                                    <span className='icon icon-chevron-thick' />
                                </button>
                            </React.Fragment>
                        ),
                        headerClassName: 'visible-xs',
                        className: 'visible-xs',
                        width: 45,
                        sortable: false,
                        accessor: 'ID',
                    },
                ],
                showPagination: false,
                getTrGroupProps: (state, row, col, instance) => {
                    const { viewIndex } = row;

                    const isExpanded =
                        state.expanded &&
                        state.expanded.hasOwnProperty(viewIndex) &&
                        state.expanded[viewIndex];

                    let classes = 'react-table__row react-table__row--selectable';
                    classes += ` ${isExpanded ? 'react-table__row--expanded' : ''}`;

                    return {
                        className: classes,
                    };
                },
                SubComponent: (row) => {
                    const {
                        ID,
                        ContainItemTypes,
                        ShippingMethod,
                        ShippingStatus,
                        Status,
                        BoxPackerParcelCount,
                        PostmenTrackingInfo,
                    } = row.original;

                    const identifier = ID;
                    const inputKey = 'ShippingStatus';
                    let hasInlineEdit = false;
                    let currentValue = row.original[inputKey];

                    if (typeof ShippingStatus == 'undefined' || ShippingStatus === null) {
                        return '';
                    }

                    if (typeof userInput[identifier] != 'undefined' && typeof userInput[identifier][inputKey] != 'undefined') {
                        hasInlineEdit = currentValue != userInput[identifier][inputKey];
                        currentValue = userInput[identifier][inputKey];
                    }

                    return (
                        <div className='react-table__extra-data'>
                            <div className='row'>
                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'CONTAINS'} />
                                    </div>
                                    <div className='react-table__info'>
                                        {ContainItemTypes}
                                    </div>
                                </div>
                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'CARRIER'} />
                                    </div>
                                    <div className='react-table__info'>
                                        {ShippingMethod}
                                    </div>
                                </div>
                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'ORDER_STATUS'} />
                                    </div>
                                    <div className='react-table__info'>
                                        {Status}
                                    </div>
                                </div>
                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'DISPATCH_STATUS'} />
                                    </div>
                                    <span className='react-table__info react-table__info--default'>
                                        <InlineFieldGroup
                                            hasUndo={true}
                                            isModified={hasInlineEdit}
                                            saveButtonLoading={isUserInputLoading(identifier, inputKey)}
                                            onSaveClick={() => saveShippingStatus(identifier, inputKey, currentValue)}
                                            onUndoClick={() => clearUserInput(identifier, inputKey)}
                                        >
                                            <FieldSelect
                                                label={getTranslation('AMOUNT')}
                                                animatedLabel={getTranslation('AMOUNT')}
                                                value={currentValue}
                                                name={inputKey}
                                                options={shippingStatusDropdown}
                                                onChange={(e) => updateUserInput(identifier, e.target.name, e.target.value)}
                                            />
                                        </InlineFieldGroup>
                                    </span>
                                </div>
                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'NO_OF_ARTICLES'} />
                                    </div>
                                    <div className='react-table__info'>
                                        {BoxPackerParcelCount}
                                    </div>
                                </div>
                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'TRACKING'} />
                                    </div>
                                    <div className='react-table__info'>
                                        {PostmenTrackingInfo}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                },
            },
        };

        return tableProps;
    };

    const applyFilter = (additionalFilterData) => {
        let updatedFilterData = {
            ...getFilters(),
        };

        if (additionalFilterData) {
            updatedFilterData = {
                ...updatedFilterData,
                ...additionalFilterData,
            };
        }

        console.log("Apply Filter ==> ", updatedFilterData);

        setBatchLoading(true);
        setTimeout(() => {
            setFilters(updatedFilterData);
            toggleRefineResultsModal(false);
            setBatchLoading(false);
        }, 0);
    };
    const exportCSV = async () => {
        let updatedListParams = JSON.parse(JSON.stringify(listParams));
        return axiosRequestDispatchList(updatedListParams)
            .then((response) => {
                const list = convertDispatchListingToAReadableFormat(
                    response.data.data.readUnManifestedOrders
                );
                const listingDisplay = convertDataToListingDisplay(
                    response.data.data.readUnManifestedOrders,
                    'ID'
                );


                const headers = columnsToExportMap.map(col =>
                    typeof col.column === 'string' ? col.column : col.key
                );

                const keys = columnsToExportMap.map(col => col.key);

                const csv = [
                    headers.join(','), // Header row
                    ...Object.values(list).map(row =>
                        keys.map(key => {
                            let value = row[key];

                            if (typeof value === 'string') {
                                return `"${value.replace(/"/g, '""')}"`;
                            }

                            return value ?? '';
                        }).join(',')
                    )
                ].join('\n');

                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `export_${Date.now()}.csv`;
                a.click();
                URL.revokeObjectURL(url);

                createAlert({ message: 'CSV file is downloaded successfully.' }, 'success');

            })
            .catch((error) => {
                handleAxiosErrors(error);
            });
    };
    return (
        <React.Fragment>
            <div className={classnames('card card--border', className)}>
                <div className='card__header'>
                    <div className='row row--vcenter-md'>
                        <div className='col-xs-12 col-md-6 vspace vspace--small'>
                            <h2 className='heading heading--lv4'>
                                <LocalizedMessage id={'DISPATCH'} />
                            </h2>
                        </div>
                        <div className='col-xs-12 col-md-6 vspace vspace--small'>
                            <div className='text-box--right'>
                                <OrderListExportButton
                                    onClick={() =>
                                        exportCSV()
                                    }
                                />
                                <RefineResultsButton
                                    onClick={() =>
                                        toggleRefineResultsModal(true)
                                    }
                                />
                                {!isLoading.list && (
                                    <button type="button" className="form__submit button" onClick={submitManifest}>
                                        <span style={{ opacity: isManifestLoading === true ? 0 : 1 }}>Submit Manifest</span>
                                        {isManifestLoading === true && (
                                            <ReactLoading
                                                className='button__spinner'
                                                type='spin'
                                                color='white'
                                                height={20}
                                                width={20}
                                            />
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {batchLoading ? (
                    <div className="loading-container">
                        <ReactLoading type='spin' color='#000' height={50} width={50} />
                    </div>
                ) : (
                    <ReactTableWrapper
                        key={getBreakpoint()}
                        loading={isLoading.list}
                        data={filteredList} // Display filtered results after batch update
                        reactTable={getTableProps()}
                        ref={(r) => (mainTableWrapper = r)}
                        perPage={99999}
                    />
                )}
            </div>
            <SectionPagination
                activePage={getActivePage()}
                totalPages={getTotalPages()}
                pages={getPaginationPages()}
                onPaginationClick={(pageNum) => setActivePage(pageNum)}
            />
            <OrderListRefineResults
                modalKey={refineResultsModalKey}
                activeFilters={getFilters()}
                applyFilter={applyFilter}
            />
        </React.Fragment>
    );
}

export default DispatchList;
